import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Search, Calendar, TrendingUp, Ticket,
    Building2, Users, Globe, MessageSquare,
    ChevronDown, Eye, AlertCircle, School, Mail, Phone,
    CircleDot, Clock, ArrowUpRight, CheckCircle2, Forward,
    LayoutGrid, UserCog, GraduationCap, Heart, GitBranch, RotateCcw, Tag,
    ChevronLeft, ChevronRight, X
} from 'lucide-react';
import { useAuthStore } from '@/shared/stores/authStore';
import { useToastStore } from '@/shared/stores/toastStore';
import { CustomBreadcrumb, CustomButton, CustomSearchableSelect } from '@/shared/components/atoms';
import { departmentApi } from '@/features/school-admin/api/departmentApi';
import { ticketApi } from '@/features/ticketing/api/ticketApi';
import { dashboardApi } from '../api/dashboardApi';
import { getMediaUrl } from '@/shared/utils/media';
import { Department } from '@/features/school-admin/types';
import { Ticket as TicketType, TicketStatus } from '@/features/ticketing/types';
import {
    DEPT_COLOR_MAP, DEPT_ICON_MAP,
} from '../data/dashboardDemoData';
import './Dashboard.css';

// ── Helpers ────────────────────────────────────────────────────
const today = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

const getTodayDateString = () => {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const DEPT_COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#14b8a6'];

function getStatusClass(status: string) {
    switch (status) {
        case 'open': return 'db-status db-status-open';
        case 'in_progress': return 'db-status db-status-inprogress';
        case 'resolved': return 'db-status db-status-resolved';
        case 'escalated': return 'db-status db-status-escalated';
        case 'forwarded': return 'db-status db-status-forwarded';
        case 'reopened': return 'db-status db-status-reopened';
        case 'overdue': return 'db-status db-status-overdue';
        default: return 'db-status db-status-open';
    }
}

function StatusBadge({ status }: { status: string }) {
    return (
        <span className={getStatusClass(status)}>
            <span className="db-status-dot" />
            {status.replace(/_/g, ' ')}
        </span>
    );
}

const getStatusColor = (status: string) => {
    switch (status) {
        case 'open': return '#16a34a';
        case 'in_progress': return '#3b82f6';
        case 'resolved': return '#0891b2';
        case 'escalated': return '#ef4444';
        case 'forwarded': return '#8b5cf6';
        case 'reopened': return '#f59e0b';
        case 'overdue': return '#e11d48';
        default: return 'var(--text-muted)';
    }
};

const getStatusDisplayName = (status: string) => {
    switch (status) {
        case 'open': return 'Open';
        case 'in_progress': return 'In Progress';
        case 'resolved': return 'Resolved';
        case 'escalated': return 'Escalated';
        case 'forwarded': return 'Forwarded';
        case 'reopened': return 'Reopened';
        case 'overdue': return 'Overdue';
        default: return status.replace(/_/g, ' ');
    }
};

const STATUS_OPTIONS = [
    { label: 'All', value: 'all' },
    { label: 'Open', value: 'open' },
    { label: 'In Progress', value: 'in_progress' },
    { label: 'Overdue', value: 'overdue' },
    { label: 'Resolved', value: 'resolved' },
    { label: 'Escalated', value: 'escalated' },
    { label: 'Forwarded', value: 'forwarded' },
    { label: 'Re Opened', value: 'reopened' }
];

const OPS_THEMES: Record<string, { color: string; bg: string }> = {
    'Total Departments': { color: '#8b5cf6', bg: 'rgba(139, 92, 246, 0.1)' },
    'Total Employees': { color: '#10b981', bg: 'rgba(16, 185, 129, 0.1)' },
    'Total Students': { color: '#3b82f6', bg: 'rgba(59, 130, 246, 0.1)' },
    'Total Parents': { color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.1)' },
    'Total Workflows': { color: '#6366f1', bg: 'rgba(99, 102, 241, 0.1)' },
    'Total Tags': { color: '#ec4899', bg: 'rgba(236, 72, 153, 0.1)' },
};

interface TicketStatusCount {
    status: string;
    count: number;
}

interface DepartmentTicketStats {
    department_name: string;
    count: number;
    status_counts: TicketStatusCount[];
    has_most_tickets?: boolean;
    has_most_open_tickets?: boolean;
    is_worst_performing?: boolean;
}

interface DashboardSchool {
    id: string;
    name: string;
    slug: string;
    logo_url?: string | null;
    banner_url?: string | null;
    primary_color?: string;
    accent_color?: string;
    total_tickets?: number;
    [key: string]: any;
}

interface DashboardData {
    total_tickets?: number;
    total_departments?: number;
    tickets_by_status?: TicketStatusCount[];
    tickets_by_department?: DepartmentTicketStats[];
    worst_performing_department?: DepartmentTicketStats;
    assigned_tickets_count?: number;
    overdue_tickets_count?: number;
    total_schools?: number;
    active_schools?: number;
    schools?: DashboardSchool[];
    [key: string]: any;
}

interface DoughnutChartProps {
    data: { status: string; count: number }[];
    total: number;
    onStatusClick?: (status: string) => void;
}

const DoughnutChart: React.FC<DoughnutChartProps> = ({ data, total, onStatusClick }) => {
    const [hoveredStatus, setHoveredStatus] = useState<string | null>(null);

    const radius = 62;
    const circumference = 2 * Math.PI * radius;
    const center = 90;

    const activeData = data.filter(d => d.count > 0);

    if (total === 0) {
        return (
            <div className="db-donut-layout">
                <div className="db-donut-svg-wrap">
                    <svg width="160" height="160" viewBox="0 0 180 180" className="db-donut-svg">
                        <circle
                            cx={center}
                            cy={center}
                            r={radius}
                            fill="none"
                            stroke="var(--border-color)"
                            strokeWidth="18"
                        />
                    </svg>
                    <div className="db-donut-center-text">
                        <span className="db-donut-center-num">0</span>
                        <span className="db-donut-center-label">Tickets</span>
                    </div>
                </div>
                <div className="db-donut-legend">
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', textAlign: 'center', padding: '1rem' }}>
                        No ticket data available.
                    </div>
                </div>
            </div>
        );
    }

    const sortedData = [...activeData].sort((a, b) => b.count - a.count);

    let accumulatedOffset = 0;

    const activeItem = hoveredStatus ? sortedData.find(d => d.status === hoveredStatus) : null;
    const centerNum = activeItem ? activeItem.count : total;
    const centerLabel = activeItem ? getStatusDisplayName(activeItem.status) : 'Total Tickets';
    const centerColor = activeItem ? getStatusColor(activeItem.status) : 'var(--text-primary)';

    return (
        <div className="db-donut-layout">
            <div className="db-donut-svg-wrap">
                <svg width="160" height="160" viewBox="0 0 180 180" className="db-donut-svg">
                    {sortedData.map((d) => {
                        const pct = d.count / total;
                        const strokeLength = pct * circumference;
                        const strokeOffset = -accumulatedOffset;
                        accumulatedOffset += strokeLength;

                        const color = getStatusColor(d.status);
                        const isHovered = hoveredStatus === d.status;

                        return (
                            <circle
                                key={d.status}
                                cx={center}
                                cy={center}
                                r={radius}
                                fill="none"
                                stroke={color}
                                strokeWidth={isHovered ? 22 : 18}
                                strokeDasharray={`${strokeLength} ${circumference}`}
                                strokeDashoffset={strokeOffset}
                                className={`db-donut-segment ${isHovered ? 'active' : ''}`}
                                style={{ opacity: hoveredStatus && !isHovered ? 0.45 : 1, cursor: 'pointer' }}
                                onMouseEnter={() => setHoveredStatus(d.status)}
                                onMouseLeave={() => setHoveredStatus(null)}
                                onClick={() => onStatusClick?.(d.status)}
                            >
                                <title>{`${getStatusDisplayName(d.status)}: ${d.count} tickets (${Math.round(pct * 100)}%) - Click to view`}</title>
                            </circle>
                        );
                    })}
                </svg>
                <div 
                    className="db-donut-center-text"
                    style={{ cursor: hoveredStatus ? 'pointer' : 'default', pointerEvents: hoveredStatus ? 'auto' : 'none' }}
                    onClick={() => hoveredStatus && onStatusClick?.(hoveredStatus)}
                >
                    <span className="db-donut-center-num" style={{ color: centerColor }}>{centerNum}</span>
                    <span className="db-donut-center-label" style={{ maxWidth: '90px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{centerLabel}</span>
                </div>
            </div>

            <div className="db-donut-legend">
                {sortedData.map((d) => {
                    const color = getStatusColor(d.status);
                    const pctVal = Math.round((d.count / total) * 100);
                    const isHovered = hoveredStatus === d.status;

                    return (
                        <div
                            key={d.status}
                            className={`db-legend-item ${isHovered ? 'active' : ''}`}
                            style={{ cursor: 'pointer' }}
                            onMouseEnter={() => setHoveredStatus(d.status)}
                            onMouseLeave={() => setHoveredStatus(null)}
                            onClick={() => onStatusClick?.(d.status)}
                            title={`Filter by ${getStatusDisplayName(d.status)}`}
                        >
                            <div className="db-legend-label-group">
                                <span className="db-legend-dot" style={{ backgroundColor: color }} />
                                <span className="db-legend-name">{getStatusDisplayName(d.status)}</span>
                            </div>
                            <div className="db-legend-val-group">
                                <span className="db-legend-count">{d.count}</span>
                                <span className="db-legend-pct">{pctVal}%</span>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

interface DepartmentBarChartProps {
    departmentsData: {
        department_id?: string;
        id?: string;
        department_name: string;
        count: number;
        status_counts: { status: string; count: number }[];
    }[];
    onBarClick?: (deptName: string, status?: string, deptId?: string) => void;
}

const DepartmentBarChart: React.FC<DepartmentBarChartProps> = ({ departmentsData, onBarClick }) => {
    const activeDepts = (departmentsData || [])
        .filter(d => d.count > 0)
        .sort((a, b) => b.count - a.count);

    if (activeDepts.length === 0) {
        return (
            <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', textAlign: 'center', padding: '3.5rem 1rem' }}>
                No department ticket data available.
            </div>
        );
    }

    const maxCount = Math.max(...activeDepts.map(d => d.count), 1);
    const barWidth = 30;
    const chartHeight = 130;
    const baselineY = 175;

    // Calculate dynamic SVG width based on number of active departments
    const columnSpacing = 60;
    const startX = 50;
    const endX = startX + activeDepts.length * columnSpacing + 20;
    const svgWidth = Math.max(endX, 420);

    const getGradientId = (status: string) => {
        switch (status) {
            case 'overdue': return 'url(#db-grad-overdue)';
            case 'open': return 'url(#db-grad-open)';
            case 'resolved': return 'url(#db-grad-resolved)';
            case 'in_progress': return 'url(#db-grad-inprogress)';
            default: return 'url(#db-grad-other)';
        }
    };

    const STATUS_ORDER = ['overdue', 'in_progress', 'open', 'resolved'];

    return (
        <div className="db-vbar-svg-container">
            <svg 
                viewBox={`0 0 ${svgWidth} 220`} 
                className="db-vbar-svg"
                style={{ minWidth: `${svgWidth}px`, width: `${svgWidth}px`, height: '220px' }}
            >
                <defs>
                    <linearGradient id="db-grad-overdue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#fb7185" />
                        <stop offset="100%" stopColor="#e11d48" />
                    </linearGradient>
                    <linearGradient id="db-grad-open" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#34d399" />
                        <stop offset="100%" stopColor="#10b981" />
                    </linearGradient>
                    <linearGradient id="db-grad-resolved" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#22d3ee" />
                        <stop offset="100%" stopColor="#0891b2" />
                    </linearGradient>
                    <linearGradient id="db-grad-inprogress" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#60a5fa" />
                        <stop offset="100%" stopColor="#3b82f6" />
                    </linearGradient>
                    <linearGradient id="db-grad-other" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#c084fc" />
                        <stop offset="100%" stopColor="#8b5cf6" />
                    </linearGradient>
                </defs>

                {/* Y-Axis Gridlines */}
                {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
                    const y = baselineY - ratio * chartHeight;
                    const val = Math.round(ratio * maxCount);
                    return (
                        <g key={ratio} opacity={0.85}>
                            <line x1="30" y1={y} x2={svgWidth - 20} y2={y} className="db-vbar-gridline" />
                            <text x="22" y={y + 3} fontSize="8" fontWeight="600" fill="var(--text-muted)" textAnchor="end">
                                {val}
                            </text>
                        </g>
                    );
                })}

                {/* Columns */}
                {activeDepts.map((dept, index) => {
                    const total = dept.count;
                    const statusCounts = dept.status_counts || [];
                    const x = startX + index * columnSpacing;
                    const deptId = (dept as any).department_id || (dept as any).id;

                    const orderedStatuses = [...statusCounts].sort((a, b) => {
                        const idxA = STATUS_ORDER.indexOf(a.status);
                        const idxB = STATUS_ORDER.indexOf(b.status);
                        const orderA = idxA === -1 ? 99 : idxA;
                        const orderB = idxB === -1 ? 99 : idxB;
                        return orderA - orderB;
                    });

                    let currentY = baselineY;
                    const labelName = dept.department_name.length > 10 
                        ? dept.department_name.slice(0, 8) + '..' 
                        : dept.department_name;

                    const tooltipText = `${dept.department_name}\nTotal: ${total} tickets\n` + 
                        orderedStatuses.map(sc => `${getStatusDisplayName(sc.status)}: ${sc.count}`).join('\n') +
                        '\n(Click to view tickets)';

                    return (
                        <g 
                            key={dept.department_name} 
                            className="db-vbar-group"
                            style={{ cursor: 'pointer' }}
                            onClick={() => onBarClick?.(dept.department_name, undefined, deptId)}
                        >
                            <title>{tooltipText}</title>
                            
                            {/* Stacked rects */}
                            {orderedStatuses.map((sc) => {
                                if (sc.count <= 0) return null;
                                const segHeight = (sc.count / maxCount) * chartHeight;
                                const rectY = currentY - segHeight;
                                currentY = rectY;

                                return (
                                    <rect
                                        key={sc.status}
                                        x={x}
                                        y={rectY}
                                        width={barWidth}
                                        height={segHeight}
                                        fill={getGradientId(sc.status)}
                                        className="db-vbar-rect"
                                        rx={1.5}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onBarClick?.(dept.department_name, sc.status, deptId);
                                        }}
                                    >
                                        <title>{`${dept.department_name} - ${getStatusDisplayName(sc.status)}: ${sc.count} tickets (Click to filter)`}</title>
                                    </rect>
                                );
                            })}

                            {/* Total volume text */}
                            <text
                                x={x + barWidth / 2}
                                y={currentY - 7}
                                className="db-vbar-text-val"
                                style={{ cursor: 'pointer' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onBarClick?.(dept.department_name, undefined, deptId);
                                }}
                            >
                                {total}
                            </text>

                            {/* Label */}
                            <text
                                x={x + barWidth / 2}
                                y={192}
                                className="db-vbar-text-label"
                                transform={`rotate(-15, ${x + barWidth / 2}, 192)`}
                                textAnchor="middle"
                                style={{ cursor: 'pointer' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onBarClick?.(dept.department_name, undefined, deptId);
                                }}
                            >
                                {labelName}
                            </text>
                        </g>
                    );
                })}

                {/* Baseline */}
                <line x1="30" y1={baselineY} x2={svgWidth - 20} y2={baselineY} className="db-vbar-axis" />
            </svg>
        </div>
    );
};

// ══════════════════════════════════════════════════════════════
//  DASHBOARD
// ══════════════════════════════════════════════════════════════
export const Dashboard: React.FC = () => {
    const navigate = useNavigate();
    const { showToast } = useToastStore();
    const { user, tenant } = useAuthStore();
    const role = user?.role?.toUpperCase() ?? 'SCHOOL_ADMIN';
    const schoolSlug = tenant?.slug || user?.school?.slug || 'school';

    // Departments scroll ref & handler
    const deptGridRef = React.useRef<HTMLDivElement>(null);
    const scrollDepartments = (direction: 'left' | 'right') => {
        if (deptGridRef.current) {
            const scrollAmount = direction === 'left' ? -220 : 220;
            deptGridRef.current.scrollBy({
                left: scrollAmount,
                behavior: 'smooth'
            });
        }
    };

    // Departments
    const [departments, setDepartments] = useState<Department[]>([]);
    const [deptLoading, setDeptLoading] = useState(true);

    // Tickets
    const [tickets, setTickets] = useState<TicketType[]>([]);
    const [ticketsLoading, setTicketsLoading] = useState(true);
    const [totalTickets, setTotalTickets] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [page, setPage] = useState(1);
    const [pageSize] = useState(10);

    // Filters
    const [search, setSearch] = useState('');
    const [deptFilter, setDeptFilter] = useState<string>('all');
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');

    const handleStartDateChange = (val: string) => {
        const todayStr = getTodayDateString();
        if (val && val > todayStr) {
            showToast('Start date cannot be in the future', 'error');
            return;
        }
        if (val && endDate && val > endDate) {
            showToast('Start date cannot be after end date', 'error');
            return;
        }
        setStartDate(val);
    };

    const handleEndDateChange = (val: string) => {
        const todayStr = getTodayDateString();
        if (val && val > todayStr) {
            showToast('End date cannot be in the future', 'error');
            return;
        }
        if (val && startDate && val < startDate) {
            showToast('End date cannot be before start date', 'error');
            return;
        }
        setEndDate(val);
    };

    // Dashboard API data
    const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
    const [dashboardLoading, setDashboardLoading] = useState(true);
    const [dashboardError, setDashboardError] = useState<string | null>(null);

    // Build stats from dashboard API data
    const getStatsFromApi = (): { label: string; value: number | string; trend: string; color: string; icon: React.ReactNode }[] => {
        if (!dashboardData) return [];

        const statusCounts: Record<string, number> = {};
        (dashboardData.tickets_by_status || []).forEach((s: TicketStatusCount) => {
            statusCounts[s.status] = s.count;
        });

        if (role === 'SUPER_ADMIN') {
            return [
                { label: 'Total Schools', value: dashboardData.total_schools ?? 0, trend: '', color: '#818cf8', icon: <School size={16} /> },
                { label: 'Active Schools', value: dashboardData.active_schools ?? 0, trend: '', color: '#10b981', icon: <CheckCircle2 size={16} /> },
            ];
        }

        if (role === 'EMPLOYEE') {
            return [
                { label: 'Assigned Tickets', value: dashboardData.assigned_tickets_count ?? 0, trend: '', color: '#818cf8', icon: <Ticket size={16} /> },
                { label: 'Overdue Tickets', value: dashboardData.overdue_tickets_count ?? 0, trend: '', color: '#ef4444', icon: <Clock size={16} /> },
                { label: 'Unassigned', value: statusCounts['unassigned'] ?? 0, trend: '', color: '#f59e0b', icon: <CircleDot size={16} /> },
                { label: 'Resolved', value: statusCounts['resolved'] ?? 0, trend: '', color: '#10b981', icon: <CheckCircle2 size={16} /> },
            ];
        }

        return [
            { label: 'Total Tickets', value: dashboardData.total_tickets ?? 0, trend: '', color: '#f59e0b', icon: <Ticket size={16} /> },
            { label: 'Open', value: statusCounts['open'] ?? 0, trend: '', color: '#16a34a', icon: <CircleDot size={16} /> },
            { label: 'In Progress', value: statusCounts['in_progress'] ?? 0, trend: '', color: '#3b82f6', icon: <Clock size={16} /> },
            { label: 'Overdue', value: statusCounts['overdue'] ?? 0, trend: '', color: '#dc2626', icon: <AlertCircle size={16} /> },
            { label: 'Forwarded', value: statusCounts['forwarded'] ?? 0, trend: '', color: '#8b5cf6', icon: <Forward size={16} /> },
            { label: 'Escalated', value: statusCounts['escalated'] ?? 0, trend: '', color: '#ef4444', icon: <ArrowUpRight size={16} /> },
            { label: 'Resolved', value: statusCounts['resolved'] ?? 0, trend: '', color: '#0891b2', icon: <CheckCircle2 size={16} /> },
        ];
    };

    const stats = getStatsFromApi();

    const [isRefreshing, setIsRefreshing] = useState(false);

    const fetchDepartments = useCallback(async () => {
        if (role === 'SUPER_ADMIN') {
            setDeptLoading(false);
            return;
        }
        setDeptLoading(true);
        try {
            const response = await departmentApi.getDepartments(schoolSlug, { page: 1, page_size: 50 });
            if (response.success && response.data) {
                const data = response.data as unknown as Department[] | { items: Department[] };
                const list: Department[] = Array.isArray(data) ? data : (data?.items || []);
                setDepartments(list);
            }
        } catch (error) {
            console.error('Failed to load departments', error);
        } finally {
            setDeptLoading(false);
        }
    }, [schoolSlug, role]);

    // Fetch departments (not for super admin)
    useEffect(() => {
        if (user && schoolSlug && schoolSlug !== 'school' && role !== 'EMPLOYEE') {
            fetchDepartments();
        } else {
            setDeptLoading(false);
        }
    }, [schoolSlug, role, user, fetchDepartments]);

    // Fetch dashboard data from API
    const fetchDashboardStats = useCallback(async (signal?: AbortSignal) => {
        if (!user) return;
        if (role !== 'SUPER_ADMIN' && (!schoolSlug || schoolSlug === 'school')) {
            return;
        }

        // Only fetch if both are set or both are empty
        if (!!startDate !== !!endDate) {
            return;
        }

        setDashboardLoading(true);
        setDashboardError(null);
        try {
            const response = role === 'SUPER_ADMIN'
                ? await dashboardApi.getSuperAdminDashboard(startDate || undefined, endDate || undefined, signal)
                : await dashboardApi.getSchoolDashboard(schoolSlug, startDate || undefined, endDate || undefined, signal);

            // Ignore cancelled responses (effect re-ran before request completed)
            if (response.error === 'cancelled') return;

            if (response.success && response.data) {
                setDashboardData(response.data);
            } else {
                setDashboardError(response.error || 'Failed to load dashboard data');
            }
        } catch (error) {
            const err = error as { name?: string; code?: string };
            if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;
            console.error('Failed to load dashboard data', error);
            setDashboardError('Unable to connect to server. Please try again later.');
        } finally {
            if (!signal || !signal.aborted) setDashboardLoading(false);
        }
    }, [schoolSlug, role, user, startDate, endDate]);

    useEffect(() => {
        const controller = new AbortController();
        fetchDashboardStats(controller.signal);
        return () => controller.abort();
    }, [fetchDashboardStats]);

    // Fetch tickets (not for super admin)
    const fetchTickets = useCallback(async () => {
        if (!user) return;
        if (!schoolSlug || role === 'SUPER_ADMIN' || schoolSlug === 'school') {
            if (role === 'SUPER_ADMIN') setTicketsLoading(false);
            return;
        }
        setTicketsLoading(true);
        try {
            const filters: Record<string, string | number> = {
                page,
                page_size: pageSize,
            };
            if (statusFilter !== 'all') filters.status = statusFilter;
            if (deptFilter !== 'all') filters.department_id = deptFilter;
            if (user?.role?.toUpperCase() === 'EMPLOYEE') filters.employee_id = user?.user_id;

            const response = await ticketApi.getAdminTickets(schoolSlug, filters);
            if (response.success && response.data) {
                setTickets(response.data.items || []);
                setTotalTickets(response.data.total_count || 0);
                setTotalPages(response.data.total_pages || 1);
            }
        } catch (error) {
            console.error('Failed to load tickets', error);
        } finally {
            setTicketsLoading(false);
        }
    }, [schoolSlug, page, pageSize, statusFilter, deptFilter, user, role]);

    useEffect(() => {
        fetchTickets();
    }, [fetchTickets]);

    const handleRefresh = useCallback(async () => {
        setIsRefreshing(true);
        try {
            const promises = [];
            promises.push(fetchDashboardStats());
            if (user && schoolSlug && schoolSlug !== 'school' && role !== 'EMPLOYEE' && role !== 'SUPER_ADMIN') {
                promises.push(fetchDepartments());
            }
            promises.push(fetchTickets());
            await Promise.all(promises);
        } catch (error) {
            console.error('Refresh failed', error);
        } finally {
            setIsRefreshing(false);
        }
    }, [fetchDashboardStats, fetchDepartments, fetchTickets, user, schoolSlug, role]);

    // Local search filter
    const filteredTickets = tickets.filter(t =>
        !search.trim() ||
        (t.subject?.toLowerCase() || '').includes(search.toLowerCase()) ||
        (t.id?.toLowerCase() || '').includes(search.toLowerCase()) ||
        (t.students?.name?.toLowerCase() || '').includes(search.toLowerCase())
    );

    // role-aware greeting/subtitle
    const greeting =
        role === 'SUPER_ADMIN' ? 'Platform Overview' :
            role === 'EMPLOYEE' ? 'My Ticket Queue' :
                'Admin Dashboard';

    // const subtitle =
    //     role === 'SUPER_ADMIN' ? `All schools` :
    //         role === 'EMPLOYEE' ? `Assigned to ${user?.name ?? 'you'}` :
    //             `${user?.school?.name ?? 'Your School'} · Ticket Management System`;

    const snapshotCards =
        role === 'SUPER_ADMIN' ? [
            { label: 'Total Schools', value: dashboardData?.total_schools ?? 0, icon: <School size={16} />, desc: 'Total schools registered on the Raabta platform.' },
            { label: 'Active Schools', value: dashboardData?.active_schools ?? 0, icon: <CheckCircle2 size={16} />, desc: 'Schools with active subscription and operations.' },
        ] : role === 'EMPLOYEE' ? [
            { label: 'Assigned Tickets', value: dashboardData?.assigned_tickets_count ?? 0, icon: <Ticket size={16} />, desc: 'Tickets currently assigned to your queue.' },
            { label: 'Overdue Tickets', value: dashboardData?.overdue_tickets_count ?? 0, icon: <Clock size={16} />, desc: 'Tickets that have exceeded their target resolution time.' },
        ] : [
            { label: 'Total Departments', value: dashboardData?.total_departments ?? departments.length, icon: <LayoutGrid size={16} />, desc: 'Registered departments serving school operations.' },
            { label: 'Total Employees', value: dashboardData?.total_employees ?? 0, icon: <UserCog size={16} />, desc: 'Active staff members resolving parent tickets.' },
            { label: 'Total Students', value: dashboardData?.total_students ?? 0, icon: <GraduationCap size={16} />, desc: 'Enrolled students linked to parent profiles.' },
            { label: 'Total Parents', value: dashboardData?.total_parents ?? 0, icon: <Heart size={16} />, desc: 'Onboarded parent accounts accessing the portal.' },
            { label: 'Total Workflows', value: dashboardData?.total_workflows ?? 0, icon: <GitBranch size={16} />, desc: 'Active approval and escalation routing flows.' },
            { label: 'Total Tags', value: dashboardData?.total_tags ?? 0, icon: <Tag size={16} />, desc: 'Labels used for classifying ticket queries.' },
        ];

    return (
        <div className="db-page">
            {/* ── Breadcrumb ── */}
            <CustomBreadcrumb
                items={[
                    { label: 'Home', path: '/' },
                    { label: greeting },
                ]}
            />

            {/* ── Page header ── */}
            <div style={{ marginTop: '0.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
                    <div>
                        <h1 style={{ fontSize: '1.6rem', fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            {role === 'SUPER_ADMIN' && <Globe size={24} color="var(--accent-primary)" />}
                            {role === 'SCHOOL_ADMIN' && <Building2 size={24} color="var(--accent-primary)" />}
                            {role === 'EMPLOYEE' && <Users size={24} color="var(--accent-primary)" />}
                            <span>{greeting}</span>
                        </h1>
                        {/* <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginTop: '0.2rem', marginBottom: 0 }}>{subtitle}</p> */}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: 500, whiteSpace: 'nowrap' }}>
                            <Calendar size={13} />
                            Filter Details By Date:
                        </span>
                        <div className="db-date-input-group">
                            <span className="db-date-input-label">From</span>
                            <input
                                type="date"
                                className="db-date-input"
                                value={startDate}
                                onChange={(e) => handleStartDateChange(e.target.value)}
                                max={endDate || getTodayDateString()}
                                title="Filter tickets created from this date"
                            />
                        </div>
                        <div className="db-date-input-group">
                            <span className="db-date-input-label">To</span>
                            <input
                                type="date"
                                className="db-date-input"
                                value={endDate}
                                onChange={(e) => handleEndDateChange(e.target.value)}
                                min={startDate}
                                max={getTodayDateString()}
                                title="Filter tickets created up to this date"
                            />
                        </div>
                        {(startDate || endDate) && (
                            <button
                                className="db-date-clear-btn"
                                onClick={() => { setStartDate(''); setEndDate(''); }}
                                title="Clear date filter"
                            >
                                <X size={12} />
                                Clear
                            </button>
                        )}
                        <button
                            className={`db-refresh-btn ${isRefreshing ? 'spinning' : ''}`}
                            onClick={handleRefresh}
                            disabled={isRefreshing}
                            title="Refresh dashboard data"
                            aria-label="Refresh dashboard data"
                        >
                            <RotateCcw size={13} />
                            Refresh
                        </button>
                    </div>
                </div>
            </div>

            {/* ── Stats row ── */}
            {dashboardLoading ? (
                <div className="db-stats-row">
                    {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="db-stat-card" style={{ '--stat-accent': '#e2e8f0' } as React.CSSProperties}>
                            <div style={{ height: '24px', width: '40px', borderRadius: '6px', background: 'linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)', backgroundSize: '800px 100%', animation: 'pulse 1.5s ease-in-out infinite' }} />
                            <div style={{ height: '12px', width: '80px', borderRadius: '4px', background: 'linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)', backgroundSize: '800px 100%', animation: 'pulse 1.5s ease-in-out infinite', marginTop: '0.5rem' }} />
                        </div>
                    ))}
                </div>
            ) : dashboardError ? (
                <div style={{
                    padding: '1.5rem', borderRadius: '12px', background: 'rgba(239,68,68,0.05)',
                    border: '1px solid rgba(239,68,68,0.2)', display: 'flex', alignItems: 'center',
                    gap: '0.75rem', marginBottom: '1.75rem'
                }}>
                    <AlertCircle size={20} color="#ef4444" />
                    <div>
                        <div style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--text-primary)' }}>Failed to load dashboard</div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{dashboardError}</div>
                    </div>
                </div>
            ) : (
                <div className="db-stats-row">
                    {/* {stats.map((s) => (
                        <div
                            key={s.label}
                            className="db-stat-card"
                            style={{ '--stat-accent': s.color } as React.CSSProperties}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <div className="db-stat-value">{s.value}</div>
                                <div style={{ color: s.color, opacity: 0.7 }}>{s.icon}</div>
                            </div>
                            <div className="db-stat-label">{s.label}</div>
                            <div className="db-stat-trend">{s.trend}</div>
                        </div>
                    ))} */}
                </div>
            )}

            {/* ── Interactive Analytics & Insights Section (School Admin only) ── */}
            {/* ── Interactive Analytics & Insights Section (School Admin only) ── */}
            {role === 'SCHOOL_ADMIN' && !dashboardLoading && !dashboardError && dashboardData && (
                <>
                    {/* Chart visualizations row */}
                    <div className="db-charts-grid">
                        <div className="db-chart-card db-chart-card--donut">
                            <div className="db-chart-header">
                                <div>
                                    <div className="db-chart-title">
                                        <CircleDot size={16} color="var(--accent-primary)" />
                                        Ticket Status Breakdown
                                    </div>
                                    <div className="db-chart-subtitle">Distribution of tickets across system states</div>
                                </div>
                            </div>
                            <DoughnutChart 
                                data={dashboardData.tickets_by_status || []} 
                                total={dashboardData.total_tickets || 0}
                                onStatusClick={(status) => navigate(`/${schoolSlug}/tickets?status=${status}`)}
                            />
                        </div>

                        <div className="db-chart-card">
                            <div className="db-chart-header">
                                <div>
                                    <div className="db-chart-title">
                                        <TrendingUp size={16} color="var(--accent-primary)" />
                                        Department Workload Analysis
                                    </div>
                                    <div className="db-chart-subtitle">Ticket volume and status distribution (top departments)</div>
                                </div>
                            </div>
                            <DepartmentBarChart 
                                departmentsData={dashboardData.tickets_by_department || []} 
                                onBarClick={(deptName, status, deptId) => {
                                    const foundDept = departments.find(
                                        d => (deptId && d.id === deptId) || (d.name && d.name.toLowerCase() === deptName.toLowerCase())
                                    );
                                    const targetDeptId = foundDept?.id || deptId;

                                    const params = new URLSearchParams();
                                    if (targetDeptId) params.append('department_id', targetDeptId);
                                    if (status) params.append('status', status);

                                    const queryString = params.toString();
                                    navigate(`/${schoolSlug}/tickets${queryString ? `?${queryString}` : ''}`);
                                }}
                            />
                        </div>
                    </div>

                    {/* Key Performance Insights & Alerts (shown below charts only if active alerts exist) */}
                    {(() => {
                        const overdueCount = (dashboardData.tickets_by_status || []).find((s: TicketStatusCount) => s.status === 'overdue')?.count ?? 0;
                        const totalCount = dashboardData.total_tickets ?? 0;
                        const ratio = totalCount > 0 ? (overdueCount / totalCount) * 100 : 0;
                        const hasWorstDept = !!dashboardData.worst_performing_department;
                        const hasUnassigned = typeof dashboardData.unassigned_tickets_count === 'number' && dashboardData.unassigned_tickets_count > 0;
                        const hasSlaRisk = ratio > 50;

                        if (!hasWorstDept && !hasUnassigned && !hasSlaRisk) {
                            return null;
                        }

                        return (
                            <>
                                <div className="db-section-header" style={{ marginTop: '0.5rem' }}>
                                    <h2 className="db-section-title">Key Performance Insights & Alerts</h2>
                                </div>
                                <div className="db-insights-grid">
                                    {/* Insight 1: Worst performing department */}
                                    {dashboardData.worst_performing_department && (
                                        <div className="db-insight-card db-insight-card-danger">
                                            <div style={{ display: 'flex', gap: '0.85rem', alignItems: 'center', minWidth: 0, flex: 1 }}>
                                                <div className="db-insight-icon-wrap">
                                                    <AlertCircle size={20} />
                                                </div>
                                                <div className="db-insight-content" style={{ minWidth: 0 }}>
                                                    <div className="db-insight-header-row">
                                                        <span className="db-insight-title">Department Needs Help</span>
                                                        <span className="db-insight-badge">Busy</span>
                                                    </div>
                                                    <p className="db-insight-desc">
                                                        The <strong>{dashboardData.worst_performing_department.department_name}</strong> has the most overdue requests (<strong>{dashboardData.worst_performing_department.count}</strong> tickets). Please check in with them to see if they need assistance.
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="db-insight-highlight-box">
                                                <span className="db-insight-highlight-val">{dashboardData.worst_performing_department.count}</span>
                                                <span className="db-insight-highlight-lbl">Overdue</span>
                                            </div>
                                        </div>
                                    )}

                                    {/* Insight 2: Unassigned tickets queue */}
                                    {hasUnassigned && (
                                        <div className="db-insight-card db-insight-card-warning">
                                            <div style={{ display: 'flex', gap: '0.85rem', alignItems: 'center', minWidth: 0, flex: 1 }}>
                                                <div className="db-insight-icon-wrap">
                                                    <Ticket size={20} />
                                                </div>
                                                <div className="db-insight-content" style={{ minWidth: 0 }}>
                                                    <div className="db-insight-header-row">
                                                        <span className="db-insight-title">New Unassigned Requests</span>
                                                        <span className="db-insight-badge">Unassigned</span>
                                                    </div>
                                                    <p className="db-insight-desc">
                                                        There are currently <strong>{dashboardData.unassigned_tickets_count}</strong> tickets that haven&apos;t been assigned to anyone. Please assign them to support staff soon so they get answered.
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="db-insight-highlight-box">
                                                <span className="db-insight-highlight-val">{dashboardData.unassigned_tickets_count}</span>
                                                <span className="db-insight-highlight-lbl">New</span>
                                            </div>
                                        </div>
                                    )}

                                    {/* Insight 3: SLA Overdue Backlog Risk */}
                                    {hasSlaRisk && (
                                        <div className="db-insight-card db-insight-card-danger">
                                            <div style={{ display: 'flex', gap: '0.85rem', alignItems: 'center', minWidth: 0, flex: 1 }}>
                                                <div className="db-insight-icon-wrap">
                                                    <TrendingUp size={20} />
                                                </div>
                                                <div className="db-insight-content" style={{ minWidth: 0 }}>
                                                    <div className="db-insight-header-row">
                                                        <span className="db-insight-title">High Overdue Rate</span>
                                                        <span className="db-insight-badge">Attention Needed</span>
                                                    </div>
                                                    <p className="db-insight-desc">
                                                        Over <strong>{Math.round(ratio)}%</strong> of all requests (<strong>{overdueCount}</strong> of <strong>{totalCount}</strong>) are overdue. You may need to assign extra help to clear the backlog.
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="db-insight-highlight-box">
                                                <span className="db-insight-highlight-val">{Math.round(ratio)}%</span>
                                                <span className="db-insight-highlight-lbl">Overdue</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </>
                        );
                    })()}
                </>
            )}

            {/* ── Super Admin: Schools List ── */}
            {role === 'SUPER_ADMIN' && dashboardLoading && (
                <div style={{ marginTop: '1.5rem' }}>
                    <p className="db-dept-section-label">Registered Schools</p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '1rem' }}>
                        {[1, 2].map((i) => (
                            <div key={i} style={{
                                background: 'var(--card-bg)', borderRadius: '14px',
                                border: '1px solid var(--border-color)', overflow: 'hidden'
                            }}>
                                <div style={{ height: '60px', background: 'linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)', backgroundSize: '800px 100%', animation: 'pulse 1.5s ease-in-out infinite' }} />
                                <div style={{ padding: '1rem', display: 'flex', gap: '0.75rem' }}>
                                    <div style={{ width: '42px', height: '42px', borderRadius: '10px', background: 'linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)', backgroundSize: '800px 100%', animation: 'pulse 1.5s ease-in-out infinite', marginTop: '-1.5rem', flexShrink: 0 }} />
                                    <div style={{ flex: 1 }}>
                                        <div style={{ height: '14px', width: '60%', borderRadius: '4px', background: 'linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)', backgroundSize: '800px 100%', animation: 'pulse 1.5s ease-in-out infinite', marginBottom: '0.5rem' }} />
                                        <div style={{ height: '10px', width: '80%', borderRadius: '4px', background: 'linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)', backgroundSize: '800px 100%', animation: 'pulse 1.5s ease-in-out infinite', marginBottom: '0.4rem' }} />
                                        <div style={{ height: '10px', width: '50%', borderRadius: '4px', background: 'linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)', backgroundSize: '800px 100%', animation: 'pulse 1.5s ease-in-out infinite' }} />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            {role === 'SUPER_ADMIN' && !dashboardLoading && dashboardData?.schools && dashboardData.schools.length > 0 && (
                <div style={{ marginTop: '2rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
                        <p className="db-dept-section-label" style={{ margin: 0 }}>Registered Schools</p>
                        <CustomButton
                            variant="outline"
                            color="secondary"
                            size="small"
                            onClick={() => navigate('/super-admin/tenants')}
                        >
                            Manage Schools
                        </CustomButton>
                    </div>
                    <div className="db-school-grid">
                        {dashboardData.schools.map((school: DashboardSchool) => {
                            const logoUrl = getMediaUrl(school.logo_url);
                            const bannerUrl = school.banner_url ? getMediaUrl(school.banner_url) : null;
                            const primaryColor = school.primary_color || '#6366f1';
                            return (
                                <div
                                    key={school.id}
                                    className="db-school-card"
                                    style={{ '--school-accent': primaryColor } as React.CSSProperties}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => navigate(`/super-admin/tenants/${school.slug}`)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            navigate(`/super-admin/tenants/${school.slug}`);
                                        }
                                    }}
                                >
                                    {/* Banner */}
                                    <div
                                        className="db-school-banner"
                                        style={{
                                            background: bannerUrl
                                                ? `url(${bannerUrl}) center/cover`
                                                : `linear-gradient(135deg, ${primaryColor}, ${school.accent_color || '#818cf8'})`,
                                        }}
                                    >
                                        <span className="db-school-status">Active</span>
                                    </div>

                                    {/* Logo */}
                                    <div
                                        className="db-school-logo-container"
                                        style={{ background: logoUrl ? 'white' : primaryColor }}
                                    >
                                        {logoUrl ? (
                                            <img src={logoUrl} alt={school.name} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                        ) : (
                                            <School size={20} color="white" />
                                        )}
                                    </div>

                                    {/* Content */}
                                    <div className="db-school-content">
                                        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '0.5rem', flexWrap: 'wrap' }}>
                                            <h3 className="db-school-title">{school.name}</h3>
                                            <span className="db-school-slug">/{school.slug}</span>
                                        </div>

                                        {school.tagline && (
                                            <p className="db-school-tagline">{school.tagline}</p>
                                        )}

                                        <div className="db-school-details">
                                            {school.support_email && (
                                                <div className="db-school-detail-item">
                                                    <Mail size={12} color="var(--accent-primary)" style={{ flexShrink: 0 }} />
                                                    <span>{school.support_email}</span>
                                                </div>
                                            )}
                                            {school.support_phone && (
                                                <div className="db-school-detail-item">
                                                    <Phone size={12} color="var(--accent-primary)" style={{ flexShrink: 0 }} />
                                                    <span>{school.support_phone}</span>
                                                </div>
                                            )}
                                        </div>

                                        <div className="db-school-date">
                                            <Calendar size={11} />
                                            <span>Joined {new Date(school.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* ── Department cards (School Admin only) ── */}
            {role !== 'EMPLOYEE' && role !== 'SUPER_ADMIN' && (
                <>
                    <div className="db-section-header" style={{ marginTop: '1.75rem' }}>
                        <h2 className="db-section-title">
                            Departments
                            <span style={{
                                marginLeft: '0.5rem',
                                fontSize: '0.85rem',
                                fontWeight: 500,
                                color: 'var(--text-primary)',
                                background: 'var(--bg-secondary)',
                                border: '1px solid var(--border-color)',
                                borderRadius: '20px',
                                padding: '0.1rem 0.6rem',
                                verticalAlign: 'middle',
                            }}>
                                {dashboardData?.total_departments ?? departments.length}
                            </span>
                        </h2>
                    </div>
                    <div className="db-dept-grid-wrapper">
                        <div className="db-dept-fade-overlay" />
                        <div className="db-dept-grid" ref={deptGridRef}>
                            {deptLoading ? (
                                <div style={{ gridColumn: 'span 4', textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>Loading departments...</div>
                            ) : !Array.isArray(departments) || departments.length === 0 ? (
                                <div style={{ gridColumn: 'span 4', textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>No departments found.</div>
                            ) : (
                                (() => {
                                    interface LocalTicketStatusCount {
                                        status: string;
                                        count: number;
                                    }
                                    interface LocalDepartmentTicketStats {
                                        department_name: string;
                                        count: number;
                                        status_counts: LocalTicketStatusCount[];
                                    }
                                    const sortedDepartments = [...departments].sort((a, b) => {
                                        const aData = (dashboardData?.tickets_by_department || []).find(
                                            (d: LocalDepartmentTicketStats) => d.department_name && a.name && d.department_name.toLowerCase() === a.name.toLowerCase()
                                        );
                                        const bData = (dashboardData?.tickets_by_department || []).find(
                                            (d: LocalDepartmentTicketStats) => d.department_name && b.name && d.department_name.toLowerCase() === b.name.toLowerCase()
                                        );
                                        const aCount = aData?.count ?? 0;
                                        const bCount = bData?.count ?? 0;
                                        return bCount - aCount;
                                    });
                                    return sortedDepartments.map((dept, index) => {
                                        const color = DEPT_COLOR_MAP[dept.name] || DEPT_COLORS[index % DEPT_COLORS.length];
                                        const icon = DEPT_ICON_MAP[dept.name] || '🏢';
                                        const deptData = (dashboardData?.tickets_by_department || []).find(
                                            (d: LocalDepartmentTicketStats) => d.department_name && dept.name && d.department_name.toLowerCase() === dept.name.toLowerCase()
                                        );
                                        const ticketCount = deptData?.count ?? 0;
                                        const statusCounts = deptData?.status_counts || [];
                                        const openCount = statusCounts.find((sc: LocalTicketStatusCount) => sc.status === 'open')?.count ?? 0;
                                        return (
                                            <div
                                                key={dept.id}
                                                className="db-dept-card"
                                                style={{ '--dept-accent': color } as React.CSSProperties}
                                                role="button"
                                                tabIndex={0}
                                                onClick={() => navigate(`/${schoolSlug}/tickets?department_id=${dept.id}`)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault();
                                                        navigate(`/${schoolSlug}/tickets?department_id=${dept.id}`);
                                                    }
                                                }}
                                            >
                                                <div
                                                    className="db-dept-icon-wrap"
                                                    style={{ background: `${color}22`, margin: 0 }}
                                                >
                                                    <span>{icon}</span>
                                                </div>
                                                <div className="db-dept-name" style={{ marginTop: '0.25rem' }}>{dept.name}</div>

                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', width: '100%', marginTop: '0.25rem', marginBottom: '0.75rem', borderTop: '1px solid var(--border-color)', paddingTop: '0.5rem' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.72rem', width: '100%' }}>
                                                        <span style={{ color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                                                            <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#16a34a', flexShrink: 0 }} />
                                                            Open Tickets
                                                        </span>
                                                        <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{openCount}</span>
                                                    </div>
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.72rem', width: '100%' }}>
                                                        <span style={{ color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                                                            <span style={{ width: 5, height: 5, borderRadius: '50%', background: color, flexShrink: 0 }} />
                                                            Total Tickets
                                                        </span>
                                                        <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{ticketCount}</span>
                                                    </div>
                                                </div>

                                                <button
                                                    className="db-get-help-btn"
                                                    style={{ background: color }}
                                                    onClick={(e) => { e.stopPropagation(); navigate(`/${schoolSlug}/tickets?department_id=${dept.id}`); }}
                                                >
                                                    View Tickets →
                                                </button>
                                            </div>
                                        );
                                    });
                                })()
                            )}
                        </div>
                    </div>
                    {!deptLoading && Array.isArray(departments) && departments.length > 0 && (
                        <div className="db-dept-nav-controls">
                            <button
                                className="db-dept-nav-btn"
                                onClick={() => scrollDepartments('left')}
                                aria-label="Scroll left"
                            >
                                <ChevronLeft size={16} />
                            </button>
                            <button
                                className="db-dept-nav-btn"
                                onClick={() => scrollDepartments('right')}
                                aria-label="Scroll right"
                            >
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    )}
                </>
            )}

            {/* ── Snapshot cards / Operations Grid ── */}
            {role === 'SCHOOL_ADMIN' && !dashboardLoading && !dashboardError && (
                <>
                    <div className="db-section-header" style={{ marginTop: '1.75rem' }}>
                        <h2 className="db-section-title">Admin Operations Snapshot</h2>
                    </div>
                    <div className="db-ops-grid">
                        {snapshotCards.map((c: { label: string; value: number | string; icon: React.ReactNode }) => {
                            const theme = OPS_THEMES[c.label] || { color: 'var(--accent-primary)', bg: 'rgba(99, 102, 241, 0.1)' };
                            let route = `/${schoolSlug}/school-admin`;
                            if (c.label === 'Total Departments') route = `/${schoolSlug}/school-admin/departments`;
                            else if (c.label === 'Total Employees') route = `/${schoolSlug}/school-admin/employees`;
                            else if (c.label === 'Total Students') route = `/${schoolSlug}/school-admin/students`;
                            else if (c.label === 'Total Parents') route = `/${schoolSlug}/school-admin/parents`;
                            else if (c.label === 'Total Workflows') route = `/${schoolSlug}/school-admin/workflows`;
                            else if (c.label === 'Total Tags') route = `/${schoolSlug}/school-admin/tags`;

                            return (
                                <div 
                                    key={c.label} 
                                    className="db-ops-card"
                                    style={{ '--ops-accent': theme.color, '--ops-bg': theme.bg } as React.CSSProperties}
                                    onClick={() => navigate(route)}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            navigate(route);
                                        }
                                    }}
                                >
                                    <div className="db-ops-icon-ring">
                                        {c.icon}
                                    </div>
                                    <div className="db-ops-info">
                                        <span className="db-ops-value">{c.value}</span>
                                        <span className="db-ops-label">{c.label.replace('Total ', '')}</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}

            {role !== 'SUPER_ADMIN' && role !== 'SCHOOL_ADMIN' && (
                <>
                    <div className="db-section-header" style={{ marginTop: '0.5rem' }}>
                        <h2 className="db-section-title">
                            {role === 'EMPLOYEE' ? 'My Queue Snapshot' : 'Admin Operations Snapshot'}
                        </h2>
                    </div>
                    <div className="db-snapshot-grid" style={{ marginBottom: '1.75rem' }}>
                        {snapshotCards.map((c: { label: string; value: number | string; icon: React.ReactNode; desc?: string }) => (
                            <div key={c.label} className="db-snapshot-card">
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <div className="db-snapshot-title">{c.label}</div>
                                    <div style={{ color: 'var(--accent-primary)', opacity: 0.6 }}>{c.icon}</div>
                                </div>
                                <div className="db-snapshot-value" style={{ marginTop: '0.5rem' }}>{c.value}</div>
                                {c.desc && (
                                    <span className="db-tooltip-text">{c.desc}</span>
                                )}
                            </div>
                        ))}
                    </div>
                </>
            )}

            {/* ── Ticket table ── */}
            {role !== 'SUPER_ADMIN' && (
                <>
                    <div className="db-section-header">
                        <h2 className="db-section-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <Ticket size={18} color="var(--accent-primary)" />
                            Recent Tickets
                        </h2>
                        <CustomButton
                            variant="outline"
                            color="secondary"
                            size="small"
                            onClick={() => navigate(`/${schoolSlug}/tickets`)}
                        >
                            View All Tickets
                        </CustomButton>
                    </div>

                    {/* Search + filter */}
                    <div className="db-search-row">
                        <div className="db-search-box">
                            <Search size={15} className="db-search-icon" />
                            <input
                                type="text"
                                placeholder="Search by, subject, or student name..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                        <div style={{ width: '260px', position: 'relative', zIndex: 10 }}>
                            <CustomSearchableSelect
                                placeholder="Filter by Department"
                                options={[
                                    { label: 'All Departments', value: 'all' },
                                    ...departments.map(d => ({ label: d.name, value: d.id }))
                                ]}
                                value={deptFilter}
                                onChange={(val) => {
                                    setDeptFilter(val as string);
                                    setPage(1);
                                }}
                                hideAvatar
                            />
                        </div>
                        <div style={{ width: '220px', position: 'relative', zIndex: 9 }}>
                            <CustomSearchableSelect
                                placeholder="Filter by Status"
                                options={STATUS_OPTIONS.map(s => ({ label: s.label, value: s.value }))}
                                value={statusFilter}
                                onChange={(val) => {
                                    setStatusFilter(val as string);
                                    setPage(1);
                                }}
                                hideAvatar
                            />
                        </div>
                        <span className="db-showing-label">Showing {filteredTickets.length} of {totalTickets} tickets</span>
                    </div>

                    {/* Table */}
                    <div className="db-table-wrap">
                        <table className="db-table">
                            <thead>
                                <tr>
                                    <th style={{ width: '10%' }}>Ticket ID</th>
                                    <th style={{ width: '30%' }}>Subject</th>
                                    <th style={{ width: '16%' }}>Department</th>
                                    <th style={{ width: '14%' }}>Student</th>
                                    <th style={{ width: '14%', minWidth: '100px' }}>Status</th>
                                    <th style={{ width: '10%', minWidth: '80px' }}>Created</th>
                                </tr>
                            </thead>
                            <tbody>
                                {ticketsLoading ? (
                                    <tr>
                                        <td colSpan={6} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '2rem' }}>
                                            Loading tickets...
                                        </td>
                                    </tr>
                                ) : filteredTickets.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '2rem' }}>
                                            No tickets found.
                                        </td>
                                    </tr>
                                ) : (
                                    filteredTickets.slice(0, 10).map((t) => (
                                        <tr key={t.id} onClick={() => navigate(`/${schoolSlug}/tickets/${t.id}`)}>
                                            <td><span className="db-ticket-id">#{t.id.slice(-6).toUpperCase()}</span></td>
                                            <td>
                                                <div className="db-ticket-title">{t.subject || t.name}</div>
                                                {t.description && (
                                                    <div className="db-ticket-msg">
                                                        <MessageSquare size={11} />
                                                        {t.description.length > 60 ? t.description.slice(0, 60) + '...' : t.description}
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                <span
                                                    className="db-dept-chip"
                                                    style={{ borderLeft: `3px solid ${DEPT_COLOR_MAP[t.departments?.name || ''] ?? 'var(--accent-primary)'}` }}
                                                >
                                                    {t.departments?.name || 'General'}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="db-student-name">{t.students?.name || t.creator_details?.name || '-'}</div>
                                            </td>
                                            <td><StatusBadge status={t.status} /></td>
                                            <td style={{ color: 'var(--text-muted)', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                                                {new Date(t.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* View all link */}
                    {filteredTickets.length > 10 && (
                        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1.5rem' }}>
                            <CustomButton
                                variant="outline"
                                size="small"
                                onClick={() => navigate(`/${schoolSlug}/tickets`)}
                            >
                                View All Tickets
                            </CustomButton>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};
